import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import { ChartSankeyDataSchema, ChartSankeyUISchema, SankeyNode } from './types'
import { ChartContainer, ChartTooltip } from '@/components/shadcn/charts'
import { Sankey, Rectangle } from 'recharts'
import { IconArrowRightSmallOutline } from '@icons/IconArrowRightSmallOutline'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'

export function ChartSankeyContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: ChartSankeyUISchema
  dataSchema: ChartSankeyDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, chartConfig, emphasizeHeader } = uiSchema
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  // Extract dynamic property names from configs
  const { level1, level2, level3 } = chartConfig.nodeConfig
  const { sourceNodeId, targetNodeId, value } = chartConfig.linkConfig

  // Transform the new data structure to Recharts format
  const transformedData = {
    nodes: dataSchema.data.nodes.map((node) => ({
      name: String(node[level1]),
      id: node.id,
      level1Value: node[level1],
      level2Value: node[level2],
      level3Value: node[level3],
      originalNode: node,
      fill: chartConfig.nodeColour || 'hsl(var(--accent))',
    })),
    links: dataSchema.data.links.map((link, linkIndex) => {
      const sourceIndex = dataSchema.data.nodes.findIndex((node) => node.id === link[sourceNodeId])
      const targetIndex = dataSchema.data.nodes.findIndex((node) => node.id === link[targetNodeId])
      const colorPalette = chartConfig.linksColourPalette || ['hsl(var(--accent))']
      const colorIndex = linkIndex % colorPalette.length

      return {
        source: sourceIndex,
        target: targetIndex,
        value: link[value],
        originalLink: link,
        color: colorPalette[colorIndex],
      }
    }),
  }

  // Custom node renderer with labels
  const renderNode = (props: {
    payload: {
      name: string
      fill: string
      level1Value: unknown
      level2Value: unknown
      level3Value: unknown
      originalNode: SankeyNode
    }
    x: number
    y: number
    width: number
    height: number
  }) => {
    const { payload, x, y, width, height } = props
    return (
      <g>
        <Rectangle x={x} y={y} width={width} height={height} fill={payload.fill} />
        <text
          x={x + width + 6}
          y={y + height / 2}
          dy="0.35em"
          textAnchor="start"
          fontSize={12}
          fill="currentColor"
          className="fill-foreground"
        >
          {String(payload.level1Value)}
        </text>
      </g>
    )
  }

  // Custom link renderer with palette colors
  const renderLink = (props: {
    sourceX: number
    sourceY: number
    sourceControlX: number
    targetX: number
    targetY: number
    targetControlX: number
    linkWidth: number
    payload: {
      source: { name: string; level1Value: unknown }
      target: { name: string; level1Value: unknown }
      value: number
      color: string
    }
  }) => {
    const { sourceX, sourceY, sourceControlX, targetX, targetY, targetControlX, linkWidth, payload } = props

    const path = `M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`

    return (
      <path
        d={path}
        stroke={payload.color}
        strokeWidth={linkWidth}
        fill="none"
        opacity={0.3}
        className="transition-opacity hover:opacity-80"
      />
    )
  }

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: unknown[] }) => {
    if (!active || !payload || !payload.length) {
      return null
    }

    const payloadItem = payload[0] as {
      payload?: {
        payload?: {
          level1Value?: unknown
          level2Value?: unknown
          level3Value?: unknown
          source?: { level1Value?: unknown }
          target?: { level1Value?: unknown }
          value?: number
        }
      }
      name?: string
      value?: number
    }

    // Access the deeply nested payload data
    const nodeData = payloadItem?.payload?.payload

    if (!nodeData) {
      return null
    }

    // For nodes, show comprehensive info with dynamic property names
    if (nodeData.level1Value) {
      return (
        <div className="bg-offwhite rounded border p-2 shadow-md">
          <p className="font-medium">{String(nodeData.level1Value)}</p>
          <p className="text-sm text-muted-foreground">
            {level2}: {String(nodeData.level2Value)}
          </p>
          <p className="text-sm text-muted-foreground">
            {level3}: {String(nodeData.level3Value)}
          </p>
        </div>
      )
    }

    // For links, show source → target with flow value
    if (nodeData.source && nodeData.target) {
      const sourceData = nodeData.source as { level1Value?: unknown }
      const targetData = nodeData.target as { level1Value?: unknown }
      return (
        <div className="bg-offwhite rounded border p-2 shadow-md">
          <p className="flex items-center gap-x-1 font-medium">
            {String(sourceData.level1Value)}
            <IconArrowRightSmallOutline />
            {String(targetData.level1Value)}
          </p>
          <p className="text-sm text-muted-foreground">Flow: {nodeData.value?.toLocaleString()}</p>
        </div>
      )
    }

    return null
  }

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div className={clsx('w-full', { 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <ChartContainer className="h-full w-full" config={{}}>
          <Sankey
            data={transformedData}
            nodeWidth={20}
            nodePadding={50}
            margin={{ top: 20, right: 180, bottom: 20, left: 20 }}
            linkCurvature={0.67}
            iterations={32}
            node={renderNode}
            link={renderLink}
          >
            <ChartTooltip content={<CustomTooltip />} />
          </Sankey>
        </ChartContainer>
      </div>
    </div>
  )
}
